import React, { useContext, useState } from "react"
import { Div } from "atomize"
import { DateTime } from "luxon"
import ConfirmButtons from "../buttons/ConfirmButtons"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import Lesson from "../Lesson"
import { cancelLesson, deleteLesson } from "../utils/firestore.utils"
import { Text } from "atomize/dist"
import RadioBox from "../atoms/RadioBox"
import TextArea from "../inputs/TextArea"
import { lessonsContext } from "../providers/lessons.provider"
import { modalContext } from "../providers/modal.provider"
import { toast } from "react-toastify"
import { networkAwareMessage } from "../utils/network.utils"
import { useSyncStatus } from "../services/sync.service"
import { canCancelLesson, recoveryPartecipants } from "../utils/cancellation.utils"
import { isMatch } from "../utils/match.utils"
import HelpTip from "../help/HelpTip"

// Cosa fare di un corso in abbonamento che non si fa
const DELETE = "delete"
const CANCEL = "cancel"
const RESCHEDULE = "reschedule"

const STEP_LESSON = "lesson"
const STEP_CHOICE = "choice"
const STEP_SERIES = "series"
const STEP_NOTES = "notes"

export function DeleteLessonModal({ closeModal, lessonData }) {
	const Modal = useContext(modalContext)
	const { removeLessonFromCache, invalidateDate } = useContext(lessonsContext)

	const [isWorking, setWorkingState] = useState(false)
	const [formStep, setFormStep] = useState(STEP_LESSON)
	const [previousSteps, setPreviousSteps] = useState([])
	const [deleteSeries, setDeleteSeries] = useState(false)
	// Un corso in abbonamento che salta non è una lezione da eliminare: gli
	// abbonati l'hanno pagata, e va recuperata. Si chiede cosa è successo.
	const cancellable = canCancelLesson(lessonData)
	const [choice, setChoice] = useState(cancellable ? CANCEL : DELETE)
	// le note della lezione: è lì che si scrive perché è saltata
	const [lessonNotes, setLessonNotes] = useState(lessonData.lessonNotes ?? "")
	// la serie senza rete non si elimina: lo si dice prima di confermare
	const { online } = useSyncStatus()

	const isCancelling = choice !== DELETE
	// "Gioco io" (src/utils/match.utils.js) si elimina come una lezione, ma
	// lezione non è: le parole sono sue. Non si annulla, quindi le frasi
	// dell'annullamento non lo riguardano.
	const playing = isMatch(lessonData)

	const goTo = step => {
		setPreviousSteps(prev => [...prev, formStep])
		setFormStep(step)
	}
	const goBack = () => {
		setFormStep(previousSteps[previousSteps.length - 1])
		setPreviousSteps(prev => prev.slice(0, -1))
	}

	// lo step che viene dopo, o null se si è arrivati a confermare
	const nextStep = () => {
		if (formStep === STEP_LESSON && cancellable) return STEP_CHOICE
		if (isCancelling) return formStep === STEP_NOTES ? null : STEP_NOTES
		if (formStep !== STEP_SERIES && lessonData.recurrencyID) return STEP_SERIES
		return null
	}

	const reportError = message => error => {
		console.error(`${message}:`, error)
		const shown = networkAwareMessage(error, message)
		toast.error(shown, { toastId: shown, position: "top-center" })
	}

	const runDelete = () => {
		setWorkingState(true)
		deleteLesson(lessonData, deleteSeries)
			.then(result => {
				deleteSeries
					? console.info("Lezioni eliminate", result)
					: console.info("Lezione eliminata", result)

				const lessonDate = lessonData.lessonStart.toDate().toISOString().split("T")[0]
				removeLessonFromCache({
					schoolID: lessonData.schoolID,
					date: lessonDate,
					coachID: lessonData.coachID,
					lessonID: lessonData.lessonID,
				})
				invalidateDate(lessonDate, lessonData.schoolID)

				closeModal()
			})
			// prima finiva in console e basta: il bottone tornava com'era e la
			// lezione restava lì, senza un perché
			.catch(
				reportError(
					playing
						? "Non è stato possibile eliminare «Gioco io»"
						: "Non è stato possibile eliminare la lezione",
				),
			)
			.finally(() => setWorkingState(false))
	}

	const runCancel = () => {
		setWorkingState(true)
		cancelLesson(lessonData, { lessonNotes })
			.then(() => {
				console.info("Lezione annullata", lessonData.lessonID)
				if (choice !== RESCHEDULE) {
					closeModal()
					return
				}
				// La lezione di recupero è una lezione come le altre, con gli
				// abbonati che pagano col recupero: la data e il campo li
				// sceglie chi la fissa, nel modulo di sempre.
				Modal.open("rescheduleLesson", {
					...lessonData,
					cancelledLessonID: lessonData.lessonID,
					partecipants: recoveryPartecipants(lessonData),
					lessonNotes: `Recupero della lezione del ${DateTime.fromJSDate(
						lessonData.lessonStart.toDate(),
					).toFormat("dd/MM")}`,
					recurrencyID: "",
				})
			})
			.catch(reportError("Non è stato possibile annullare la lezione"))
			.finally(() => setWorkingState(false))
	}

	const confirm = () => {
		const step = nextStep()
		if (step) goTo(step)
		else if (isCancelling) runCancel()
		else runDelete()
	}

	const deleteTitle = playing ? "Vuoi eliminare «Gioco io»?" : "Vuoi eliminare la lezione?"
	const title =
		formStep === STEP_LESSON
			? cancellable
				? "La lezione non si fa?"
				: deleteTitle
			: formStep === STEP_CHOICE
				? "Cosa è successo?"
				: formStep === STEP_NOTES
					? choice === RESCHEDULE
						? "Annulla e riprogramma"
						: "Annulla la lezione"
					: deleteTitle

	const confirmText = isWorking
		? isCancelling
			? "Sto annullando la lezione..."
			: playing
				? "Sto eliminando..."
				: "Sto eliminando la lezione..."
		: formStep === STEP_CHOICE
			? "Avanti"
			: formStep === STEP_NOTES
				? choice === RESCHEDULE
					? "Annulla e scegli la data"
					: "Annulla la lezione"
				: cancellable && formStep === STEP_LESSON
					? "Sì, vai avanti"
					: playing
						? "Sì, elimina"
						: deleteSeries
							? "Sì, eliminale"
							: "Sì, eliminala"

	// rosso, col cestino, il tasto che elimina; non "Avanti" né "Annulla la
	// lezione", che non tolgono niente (ConfirmButtons)
	const deleting =
		!isCancelling && formStep !== STEP_CHOICE && !(cancellable && formStep === STEP_LESSON)

	return (
		<Container>
			{formStep === STEP_CHOICE ? (
				// le tre strade dette in breve, sotto il titolo, e il rimando
				// alla guida: si apre qui, perché un secondo foglio chiuderebbe
				// questo
				<HelpTip tip="corsoSalta" inline m={{ b: "0.5rem" }}>
					<Text textWeight="bold" textSize="title">
						{title}
					</Text>
				</HelpTip>
			) : (
				<Div
					d={"flex"}
					justify={"space-between"}
					m={{ b: "0.5rem" }}
					textWeight="bold"
					textSize="title"
					textColor={theme === "light" ? "text" : "text_alt"}>
					{title}
				</Div>
			)}
			{formStep === STEP_LESSON && <Lesson lessonData={lessonData} modalView={true} />}
			{formStep === STEP_CHOICE && (
				<>
					<RadioBox
						text="Annulla"
						checked={choice === CANCEL}
						handleChange={async () => setChoice(CANCEL)}
					/>
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
						Era prevista ma non si è fatta: ogni abbonato ha una lezione da recuperare
					</Text>
					<RadioBox
						text="Annulla e riprogramma"
						checked={choice === RESCHEDULE}
						handleChange={async () => setChoice(RESCHEDULE)}
					/>
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
						Come "Annulla", e scegli subito la data del recupero per tutti gli abbonati
					</Text>
					<RadioBox
						text="Elimina"
						checked={choice === DELETE}
						handleChange={async () => setChoice(DELETE)}
					/>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						Non doveva esserci, è stata inserita per sbaglio: sparisce e basta
					</Text>
				</>
			)}
			{formStep === STEP_NOTES && (
				<>
					<Text m={{ b: "0.5rem" }}>
						La lezione resta in elenco come annullata, e si può ripristinare. Chi pagava
						la volta non paga, e non ha niente da recuperare.
						{lessonData.recurrencyID && " Vale solo per questa: la serie continua."}
					</Text>
					<TextArea
						placeholder="Perché salta? Pioggia, maestro assente..."
						value={lessonNotes}
						handleChange={e => setLessonNotes(e.target.value)}
					/>
				</>
			)}
			{formStep === STEP_SERIES && (
				<>
					<Text m={{ b: "0.5rem" }}>
						{playing
							? "Giochi ogni settimana: vuoi eliminare solo questa volta o anche le prossime?"
							: "Questa è una lezione ricorrente: è possibile eliminare solo questa o anche le successive, come preferisci fare?"}
					</Text>
					<RadioBox
						text="Elimina solo questa"
						checked={deleteSeries === false}
						handleChange={async () => setDeleteSeries(false)}
					/>
					<RadioBox
						text="Elimina anche le prossime"
						checked={deleteSeries === true}
						handleChange={async () => setDeleteSeries(true)}
					/>
					{deleteSeries && !online && (
						<Text textColor={theme.colors.red} m={{ t: "0.5rem" }}>
							{playing
								? "Senza rete si può eliminare solo questa volta"
								: "Senza rete si può eliminare solo questa lezione"}
						</Text>
					)}
				</>
			)}
			<ConfirmButtons
				confirmText={confirmText}
				danger={deleting}
				busy={isWorking}
				onConfirm={confirm}
				cancelText={formStep === STEP_LESSON ? "No, macchè!" : "Indietro"}
				onCancel={formStep === STEP_LESSON ? closeModal : goBack}
			/>
		</Container>
	)
}

export default DeleteLessonModal
